import React from "react";
import { Type, Contrast, RotateCcw } from "lucide-react";
import {
  useDisplayPreferences,
  ALL_TEXT_SCALES,
} from "../hooks/useDisplayPreferences";

/* DisplayPreferences
   ------------------
   The visitor's "Display & accessibility" toolbar. Lives inline
   inside any page that wants to surface it (currently top of
   /fingerspell, /fingerspell/quiz, and the /accessibility page).
   Controls:
     - Skin tone for BSL hand graphics
     - Left-handed signing mirror
     - Text size (4 levels: 100% / 110% / 125% / 150%)
     - High contrast mode (boolean)
     - Reduce motion (boolean)
     - Reset button

   Mutations land in the shared `useDisplayPreferences` hook, which
   persists everything to localStorage and broadcasts a change event
   so every other mounted instance refreshes. The text-size, contrast
   and reduce-motion settings are applied site-wide by the App-root
   `useApplyDisplayPreferences()` hook.
*/

const TONE_SWATCHES = [
  { id: "original", label: "Light", swatch: "#F0C29A" },
  { id: "medium", label: "Medium", swatch: "#B47750" },
  { id: "deep", label: "Deep", swatch: "#5E3A23" },
];

const SCALE_LABEL = { 1: "A", 1.1: "A", 1.25: "A", 1.5: "A" };
const SCALE_PERCENT = { 1: "100%", 1.1: "110%", 1.25: "125%", 1.5: "150%" };
// Distinct accessible names – the visible glyph is the same "A" at four sizes,
// which reads identically to a screen reader. 100% is the default (smallest).
const SCALE_ARIA = {
  1: "Default text size (100%)",
  1.1: "Large text (110%)",
  1.25: "Larger text (125%)",
  1.5: "Extra-large text (150%)",
};

const DisplayPreferences = ({
  compact = false,
  signOnly = false,
  className = "",
  appearanceExtra = null,
}) => {
  const {
    skinTone, leftHanded,
    textScale, highContrast, reduceMotion,
    setSkinTone, setLeftHanded,
    setTextScale, setHighContrast, setReduceMotion,
    resetPrefs,
  } = useDisplayPreferences();

  return (
    <div
      className={`bs-prefs ${compact ? "bs-prefs--compact" : ""} ${signOnly ? "bs-prefs--sign-only" : ""} ${className}`}
      data-testid="display-prefs"
      role="group"
      aria-label={
        signOnly
          ? "Hand appearance: skin tone and handedness"
          : "Display and accessibility preferences"
      }
    >
      {/* Appearance = how the signing hands look (skin tone + handedness).
          These are personalisation / representation choices, NOT accessibility
          accommodations, so they sit under their own heading - kept clearly
          apart from the genuine accessibility controls below. */}
      {!signOnly && <p className="bs-prefs__section">Appearance</p>}
      <div className="bs-prefs__group" data-testid="display-prefs-tones">
        <span className="bs-prefs__label">Skin tone</span>
        <div className="bs-prefs__swatches" role="radiogroup" aria-label="Skin tone">
          {TONE_SWATCHES.map((t) => (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={skinTone === t.id}
              aria-label={`${t.label} skin tone`}
              title={`${t.label} skin tone`}
              onClick={() => setSkinTone(t.id)}
              className={`bs-prefs__swatch ${
                skinTone === t.id ? "is-active" : ""
              }`}
              style={{ background: t.swatch }}
              data-testid={`display-prefs-tone-${t.id}`}
            />
          ))}
        </div>
      </div>

      <div className="bs-prefs__group">
        <label className="bs-prefs__toggle" data-testid="display-prefs-lefthanded">
          <input
            type="checkbox"
            checked={leftHanded}
            onChange={(e) => setLeftHanded(e.target.checked)}
            data-testid="display-prefs-lefthanded-input"
          />
          <span className="bs-prefs__toggle-track" aria-hidden="true">
            <span className="bs-prefs__toggle-thumb" />
          </span>
          <span className="bs-prefs__toggle-text">Left-handed</span>
        </label>
      </div>

      {/* Scope note: skin tone + handedness only redraw the fingerspelling
          hand shapes. The dictionary's sign illustrations are fixed drawings
          with no tone/handedness variants, so people don't expect them to
          change and shouldn't be left wondering why they didn't. Skipped when
          appearanceExtra is present (the /accessibility page shows its own
          fuller version there, so this would duplicate it). */}
      {!signOnly && !appearanceExtra && (
        <p className="bs-prefs__hint">
          These change the fingerspelling hand shapes, not the dictionary’s
          sign illustrations.
        </p>
      )}

      {/* Optional slot rendered directly beneath the appearance controls
          (e.g. a live hand-graphics preview on /accessibility), so the
          effect of changing skin tone / handedness is visible right next
          to the swatches rather than below the whole panel. */}
      {appearanceExtra}

      {/* The text-size, contrast, reduce-motion and reset controls are
          site-wide preferences that already live in the header's
          accessibility popover. On individual game pages we only want
          the two controls that affect how the BSL hand graphics
          render (skin tone + left-handed), so callers can pass
          signOnly to hide the rest and avoid duplicating the global
          panel inline. */}
      {!signOnly && (
        <>
          {/* Genuine accessibility controls (assistive), kept separate from
              the Appearance choices above. */}
          <p className="bs-prefs__section bs-prefs__section--divide">Accessibility</p>
          <div className="bs-prefs__group" data-testid="display-prefs-text">
            <span className="bs-prefs__label">
              <Type size={13} aria-hidden="true" /> Text size
            </span>
            <div className="bs-prefs__textscale" role="radiogroup" aria-label="Text size">
              {ALL_TEXT_SCALES.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  role="radio"
                  aria-checked={textScale === s}
                  aria-label={SCALE_ARIA[s]}
                  title={`Text size ${SCALE_PERCENT[s]}`}
                  onClick={() => setTextScale(s)}
                  className={`bs-prefs__textbtn ${textScale === s ? "is-active" : ""}`}
                  style={{ fontSize: 11 + i * 2 }}
                  data-testid={`display-prefs-textscale-${String(s).replace(".", "")}`}
                >
                  {SCALE_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          <div className="bs-prefs__group">
            <label className="bs-prefs__toggle" data-testid="display-prefs-contrast">
              <input
                type="checkbox"
                checked={highContrast}
                onChange={(e) => setHighContrast(e.target.checked)}
                data-testid="display-prefs-contrast-input"
              />
              <span className="bs-prefs__toggle-track" aria-hidden="true">
                <span className="bs-prefs__toggle-thumb" />
              </span>
              <span className="bs-prefs__toggle-text">
                <Contrast size={13} aria-hidden="true" /> High contrast
              </span>
            </label>
          </div>

          <div className="bs-prefs__group">
            <label className="bs-prefs__toggle" data-testid="display-prefs-motion">
              <input
                type="checkbox"
                checked={reduceMotion}
                onChange={(e) => setReduceMotion(e.target.checked)}
                data-testid="display-prefs-motion-input"
              />
              <span className="bs-prefs__toggle-track" aria-hidden="true">
                <span className="bs-prefs__toggle-thumb" />
              </span>
              <span className="bs-prefs__toggle-text">Reduce motion</span>
            </label>
          </div>

          <button
            type="button"
            className="bs-prefs__reset"
            onClick={resetPrefs}
            data-testid="display-prefs-reset"
            title="Reset all display preferences to defaults"
          >
            <RotateCcw size={12} aria-hidden="true" />
            <span>Reset</span>
          </button>
        </>
      )}
    </div>
  );
};

export default DisplayPreferences;
